import type { LocalDate } from "@js-joda/core";
import { Api } from "services/api/api.service";
import type {
  StaffProfileData,
  UpdateStaffWorkerSalaryRequest,
} from "services/api/company/staff.service";
import {
  toIsoDateStartOfDayUtc,
  toNonNegativeNumber,
} from "../domains/employment/profilePanel.payload.logic";
import type { EmploymentDetailsForm } from "../profilePanel.contract";

export interface DraftSalaryItem {
  id: number;
  startDate: string;
  value: number | null;
}

interface AddSalaryDraftItemParams {
  editedEmploymentDetails: EmploymentDetailsForm;
  effectiveFromDate: LocalDate | null;
  nextDraftId: number;
  setSaveError: (value: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

interface DeleteSalaryDraftItemParams {
  salaryDraftHistory: DraftSalaryItem[];
  salaryId: number;
}

interface HasSalaryDraftHistoryChangesParams {
  salaryDraftHistory: DraftSalaryItem[];
  salaryInitialHistory: DraftSalaryItem[];
}

interface SaveSalaryDraftHistoryParams {
  companyId: number | null;
  currentEmploymentId: number | null;
  executeSavingAction: (action: () => Promise<void>) => void;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  reloadProfile: () => void;
  salaryDraftHistory: DraftSalaryItem[];
  salaryInitialHistory: DraftSalaryItem[];
  setModalState: (value: { type: "none" }) => void;
  setSaveError: (value: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
  workerId: number | null;
}

interface UpdateSalaryDraftValueParams {
  salaryDraftHistory: DraftSalaryItem[];
  salaryId: number;
  value: number;
}

export function toSalaryDraftHistory(
  profile: StaffProfileData | null,
): DraftSalaryItem[] {
  return (
    profile?.employmentInfo?.current?.salaries.map((salary) => ({
      id: salary.id,
      startDate: salary.startDate ?? "",
      value: salary.value,
    })) ?? []
  );
}

export function addSalaryDraftItem(
  params: AddSalaryDraftItemParams,
): DraftSalaryItem | null {
  if (params.effectiveFromDate == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return null;
  }

  return {
    id: params.nextDraftId,
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    value: toNonNegativeNumber(params.editedEmploymentDetails.salary),
  };
}

export function deleteSalaryDraftItem(
  params: DeleteSalaryDraftItemParams,
): DraftSalaryItem[] {
  return params.salaryDraftHistory.filter(
    (salary) => salary.id !== params.salaryId,
  );
}

export function hasSalaryDraftHistoryChanges(
  params: HasSalaryDraftHistoryChangesParams,
): boolean {
  return (
    JSON.stringify(params.salaryDraftHistory) !==
    JSON.stringify(params.salaryInitialHistory)
  );
}

export function updateSalaryDraftValue(
  params: UpdateSalaryDraftValueParams,
): DraftSalaryItem[] {
  return params.salaryDraftHistory.map((salary) =>
    salary.id === params.salaryId ? { ...salary, value: params.value } : salary,
  );
}

export function saveSalaryDraftHistory(
  params: SaveSalaryDraftHistoryParams,
): void {
  if (params.isSaving) return;
  if (
    params.companyId == null ||
    params.workerId == null ||
    params.currentEmploymentId == null
  ) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const initialExistingIds = new Set(
    params.salaryInitialHistory
      .filter((salary) => salary.id > 0)
      .map((salary) => salary.id),
  );
  const draftExistingIds = new Set(
    params.salaryDraftHistory
      .filter((salary) => salary.id > 0)
      .map((salary) => salary.id),
  );
  const deletedIds = Array.from(initialExistingIds).filter(
    (id) => !draftExistingIds.has(id),
  );
  const addedItems = params.salaryDraftHistory.filter(
    (salary) => salary.id < 0,
  );
  const modifiedItems = params.salaryDraftHistory.filter((salary) => {
    if (salary.id <= 0) return false;
    const initialSalary = params.salaryInitialHistory.find(
      (initialItem) => initialItem.id === salary.id,
    );
    if (initialSalary == null) return false;
    return (
      initialSalary.startDate !== salary.startDate ||
      initialSalary.value !== salary.value
    );
  });
  const hasChanges =
    deletedIds.length > 0 || addedItems.length > 0 || modifiedItems.length > 0;

  if (!hasChanges) {
    params.setModalState({ type: "none" });
    return;
  }

  const companyId = params.companyId;
  const workerId = params.workerId;
  const currentEmploymentId = params.currentEmploymentId;

  params.executeSavingAction(async () => {
    for (const salaryId of deletedIds) {
      await Api().company.staff.deleteWorkerSalary(
        companyId,
        workerId,
        currentEmploymentId,
        salaryId,
      );
    }
    for (const salary of addedItems) {
      const payload: UpdateStaffWorkerSalaryRequest = {
        salaryId: 0,
        startDate: salary.startDate,
        value: salary.value ?? 0,
      };
      await Api().company.staff.addWorkerSalary(
        companyId,
        workerId,
        payload,
        currentEmploymentId,
      );
    }
    for (const salary of modifiedItems) {
      await Api().company.staff.updateWorkerSalary(
        companyId,
        workerId,
        currentEmploymentId,
        salary.id,
        {
          startDate: salary.startDate,
          value: salary.value ?? 0,
        },
      );
    }
    params.setModalState({ type: "none" });
    params.reloadProfile();
    params.onWorkerUpdated?.();
  });
}
